/**
 * Tabs Module
 *
 * Handles tab switching with support for multiple independent tab groups.
 * Auto-initializes on elements with data-controller="tabs".
 *
 * Usage in Twig:
 * <div data-controller="tabs" data-tabs-group="settings">
 *     <nav>
 *         <a href="#general" data-tab="general" class="tab-link">General</a>
 *         <a href="#advanced" data-tab="advanced" class="tab-link">Advanced</a>
 *     </nav>
 *     <div data-tab-content="general">General content</div>
 *     <div data-tab-content="advanced" class="hidden">Advanced content</div>
 * </div>
 */

import { $, $$, on, ready } from '../utils/dom.js';

// Store for tab managers by group name
const tabManagers = {};

/**
 * Initialize tabs on a container
 * @param {Element} container - The tab container element
 */
function initTabs(container) {
    const groupName = container.dataset.tabsGroup || container.dataset.hookName || 'default';

    // Get tab links and content panels
    const getTabLinks = () => $$('[data-tab]', container);
    const getTabContents = () => $$('[data-tab-content]', container);

    // Also support legacy class-based selectors
    const getLegacyLinks = () => $$(`.tab-link-${groupName}`, container);
    const getLegacyContents = () => $$(`.tab-content-${groupName}`, container);

    /**
     * Show a specific tab
     * @param {string} tabId - The tab ID to show
     */
    function showTab(tabId) {
        // Hide all tab contents
        const contents = getTabContents();
        const legacyContents = getLegacyContents();

        [...contents, ...legacyContents].forEach(panel => {
            panel.classList.add('hidden');
            panel.style.display = 'none';
        });

        // Show selected tab content
        const selectedContent = $(`[data-tab-content="${tabId}"]`, container) ||
                               $(`#tab-${tabId}`, container);
        if (selectedContent) {
            selectedContent.classList.remove('hidden');
            selectedContent.style.display = 'block';
        }

        // Update tab link styles
        const links = getTabLinks();
        const legacyLinks = getLegacyLinks();

        [...links, ...legacyLinks].forEach(link => {
            link.classList.remove('border-blue-500', 'text-blue-600');
            link.classList.add('border-transparent', 'text-gray-500');
        });

        // Highlight active tab
        const activeLink = $(`[data-tab="${tabId}"]`, container);
        if (activeLink) {
            activeLink.classList.remove('border-transparent', 'text-gray-500');
            activeLink.classList.add('border-blue-500', 'text-blue-600');

            // Notify SettingsTabManager if available (for breadcrumb updates)
            if (window.SettingsTabManager?.setActiveTab) {
                window.SettingsTabManager.setActiveTab(tabId, activeLink.dataset.tabLabel || null);
            }
        }

        // Dispatch custom event
        container.dispatchEvent(new CustomEvent('tab:changed', {
            detail: { tabId, groupName }
        }));
    }

    /**
     * Get the currently active tab ID
     * @returns {string|null} The active tab ID
     */
    function getActiveTab() {
        const activeLink = $('[data-tab].border-blue-500', container) ||
                          $('[data-tab].text-blue-600', container);
        return activeLink?.dataset.tab || null;
    }

    // Click handler for tab links
    on(container, 'click', '[data-tab]', (e, link) => {
        e.preventDefault();
        showTab(link.dataset.tab);
    });

    // Show first tab on initialization
    const firstLink = $('[data-tab]', container) || $(`.tab-link-${groupName}`, container);
    const firstContent = $('[data-tab-content]', container) || $(`.tab-content-${groupName}`, container);

    if (firstLink && firstContent) {
        const firstTabId = firstLink.dataset.tab || firstContent.dataset.tabContent;
        if (firstTabId) {
            showTab(firstTabId);
        }
    }

    // Store the tab manager
    const manager = {
        showTab,
        getActiveTab,
        container
    };

    tabManagers[groupName] = manager;
    container._tabs = manager;

    return manager;
}

/**
 * Get a tab manager by group name
 * @param {string} groupName - The tab group name
 * @returns {Object|null} The tab manager
 */
export function getManager(groupName) {
    return tabManagers[groupName] || null;
}

/**
 * Create legacy global functions for backward compatibility
 * Called by tabs.html.twig with dynamic function names
 */
function createLegacyFunction(hookName) {
    const funcName = `showTab_${hookName}`;

    if (!window[funcName]) {
        window[funcName] = function(tabId) {
            // Find the container for this hook
            const container = $(`[data-tabs-group="${hookName}"]`) ||
                             $(`[data-hook-name="${hookName}"]`) ||
                             document.querySelector(`.tab-link-${hookName}`)?.closest('[data-controller="tabs"]');

            if (container && container._tabs) {
                container._tabs.showTab(tabId);
            } else {
                // Fallback: direct DOM manipulation for non-initialized tabs
                document.querySelectorAll(`.tab-content-${hookName}`).forEach(tab => {
                    tab.classList.add('hidden');
                    tab.style.display = 'none';
                });

                const selectedTab = document.getElementById('tab-' + tabId);
                if (selectedTab) {
                    selectedTab.classList.remove('hidden');
                    selectedTab.style.display = 'block';
                }

                document.querySelectorAll(`.tab-link-${hookName}`).forEach(tab => {
                    tab.classList.remove('border-blue-500', 'text-blue-600');
                    tab.classList.add('border-transparent', 'text-gray-500');
                });

                const activeTabLink = document.querySelector(`.tab-link-${hookName}[data-tab="${tabId}"]`);
                if (activeTabLink) {
                    activeTabLink.classList.remove('border-transparent', 'text-gray-500');
                    activeTabLink.classList.add('border-blue-500', 'text-blue-600');

                    if (window.SettingsTabManager?.setActiveTab) {
                        window.SettingsTabManager.setActiveTab(tabId, activeTabLink.dataset.tabLabel || null);
                    }
                }
            }
        };
    }
}

/**
 * Auto-initialize on page load
 */
ready(() => {
    // Initialize data-controller="tabs" elements
    $$('[data-controller="tabs"]').forEach(initTabs);

    // Create legacy functions for existing tab components
    // Find all unique hook names from tab links
    const hookNames = new Set();
    $$('[class*="tab-link-"]').forEach(el => {
        el.classList.forEach(cls => {
            const match = cls.match(/^tab-link-(.+)$/);
            if (match && match[1] !== 'default') {
                hookNames.add(match[1]);
            }
        });
    });

    hookNames.forEach(createLegacyFunction);

    // Always create default
    createLegacyFunction('default');
});

/**
 * Manual initialization
 * @param {Element|string} target - Element or selector
 */
export function init(target) {
    const element = typeof target === 'string' ? $(target) : target;
    if (element) {
        return initTabs(element);
    }
}

export default {
    init,
    getManager
};
